import React from "react";
import {ApiLink} from "../components/link";
import {CodeBlend} from "../components/code";

const options = [
    {
        name: 'OLEDBProvider',
        scope: 'api',
        description: `Forces SQLAPI++ library to use specified OLEDB provider (if not defined with the connecion string).`,
        valid: (
            <>
                empty, {' '}<code>"Microsoft.ACE.OLEDB.12.0"</code>, ...
            </>),
        defaultValue:
            <>
                empty.
            </>
    },
    {
        name: 'CoInitializeEx_COINIT',
        scope: 'api',
        description: `Specifies the COM library initialization mode.`,
        valid:
            <>
                {' '}<code>"COINIT_MULTITHREADED"</code>, {' '}<code>"COINIT_APARTMENTTHREADED"</code>,
                {' '}<code>"Skip"</code>{' '} (SQLAPI++ doesn't initialize COM),
                {' '}<code>"Default"</code>{' '} (SQLAPI++ tries to set "COINIT_MULTITHREADED" value; if it fails,
                SQLAPI++ tries to set "COINIT_APARTMENTTHREADED")
            </>,
        defaultValue: <code>"Default"</code>
    },
    {
        name: 'DBPROP_INIT_PROMPT',
        scope: 'connection',
        description: `Indicates whether to prompt the user during initialization.`,
        valid:
            <>
               <code>"DBPROMPT_NOPROMPT", "DBPROMPT_PROMPT", "DBPROMPT_COMPLETE", "DBPROMPT_COMPLETEREQUIRED"</code>
            </>,
        defaultValue: <code>"DBPROMPT_NOPROMPT"</code>
    },
        {
        name: 'DBPROP_AUTH_INTEGRATED',
        scope: 'connection',
        description: `When the connection username is empty this parameters specifies he authntication service.`,
        valid:
            <>
                The name of the authentication service used by the server to identify
                the user using the identity provided by an authentication domain. 
            </>,
        defaultValue:
            <>
                empty.
            </>
    },
    {
        name: 'DBPROP_INIT_TIMEOUT',
        scope: 'connection',
        description: `Sets the connection time out.`,
        valid:
            <>
                String containing number of seconds before a connection times out.
                A value of {' '}<code>"0"</code>{' '} indicates
                an infinite time-out.
            </>,
        defaultValue:
            <>
                By default SQLAPI++ doesn't change this option and uses the value set by OLEDB. See
                OLEDB documentation for details.
            </>
    },
    {
        name: 'PreFetchRows',
        scope: 'command',
        description: `Forces SQLAPI++ library to fetch rows in bulk, rather than retrieving records one by one.`,
        valid: 'String containing number of rows in the fetch buffer',
        defaultValue: <code>"1"</code>
    },
    {
        name: 'UseDynamicCursor',
        scope: 'command',
        description:
            <>
                Forces SQLAPI++ to use scrollable dynamic command handle. Sets
                {' '}<CodeBlend>DBPROP_OTHERINSERT</CodeBlend>{' '} = {' '}<CodeBlend>VARIANT_TRUE</CodeBlend>,
                {' '}<CodeBlend>DBPROP_OTHERUPDATEDELETE</CodeBlend> = {' '}<CodeBlend>VARIANT_TRUE</CodeBlend>,
                {' '}<CodeBlend>DBPROP_CANSCROLLBACKWARDS</CodeBlend> = {' '}<CodeBlend>VARIANT_TRUE</CodeBlend>.
            </>,
        valid: <><code>"True"</code>, {' '}<code>"1"</code></>,
        defaultValue: <code>"False"</code>
    },
    {
        name: 'ICommandPrepare',
        scope: 'command',
        description: `Controls current command preparation with ICommandPrepare interface.`,
        valid:
            <ul>
                <li><code>"skip"</code>{' '} - skips {' '}<CodeBlend>ICommandPrepare::Prepare()</CodeBlend> call</li>
                <li>
                    <code>"required"</code>{' '} - calls {' '}<CodeBlend>ICommandPrepare::Prepare()</CodeBlend>{''}
                    and reports errors if any
                </li>
                <li>
                    <code>"optional"</code>{' '} - calls {' '}<CodeBlend>ICommandPrepare::Prepare()</CodeBlend>{' '}
                    and ignores errors if any
                </li>
                <li>
                    <code>"SetParameterInfo"</code>{' '} - calls
                    {' '}<CodeBlend>ICommandWithParameters::SetParameterInfo()</CodeBlend>{' '} before
                    {' '}<CodeBlend>ICommandPrepare::Prepare()</CodeBlend>.
                    {' '}<ApiLink>SAParam</ApiLink>{' '} scale and precision should be defined for numeric parameters
                    before statement prepared.
                </li>
            </ul>,
        defaultValue: <code>"skip"</code>
    },
    {
        name: 'UseStreamForLongOrLobParameters',
        scope: 'parameter',
        description:
            <>
                Sets the IID_SequentalStream interface is used for the related parameter for the writing the long/LOB data to the server.
                Requires the OLEDB driver supports {' '}<code>[N]VARACHAR(MAX) / VARBINART(MAX)</code> data types.
                For using with {' '}<code>[N]TEXT / IMAGE</code> data types it
                also requires the option {' '}<code>"ICommandPrepare"</code>{' '} set to {' '}<code>"SetParameterInfo"</code>{ ' '}.
            </>,
        valid: <><code>"True"</code>, {' '}<code>"1"</code></>,
        defaultValue: <code>"False"</code>
    },
    {
        name: 'Execute_riid',
        scope: 'command',
        description:
            <>
                Sets the requested interface for the rowset returned by the command. See OLEDB documentation
                (<CodeBlend>ICommand::Execute()</CodeBlend> function) for details.
            </>,
        valid:
            <>
                {' '}<code>"IID_NULL"</code>{' '} (no rowset is returned),
                {' '}<code>"IID_IRowset"</code>{' '} (should be used with Compact Edition),
                {' '}<code>"IID_IStream"</code>,
                {' '}<code>"IID_ISequentialStream"</code>,
                {' '}<code>"IID_IMultipleResults"</code>
            </>,
        defaultValue: <><code>"IID_IMultipleResults"</code>{' '} (to create multiple results)</>
    },
    {
        name: 'DBPROP_COMMANDTIMEOUT',
        scope: 'command',
        description: `Sets the command time out.`,
        valid:
            <>
                String containing number of seconds before a command times out. A value of
                {' '}<code>"0"</code>{' '} indicates an infinite time-out.
            </>,
        defaultValue:
            <>
                By default SQLAPI++ doesn't change this option and uses the value set by OLEDB. See
                OLEDB documentation for details.
            </>
    },
    {
        name: 'DBPROP_COMMITPRESERVE',
        scope: 'command',
        description: `Determines the behavior of a rowset after a commit operation.`,
        valid: <><code>"VARIANT_TRUE"</code>, {' '}<code>"VARIANT_FALSE"</code></>,
        defaultValue:
            <>
                By default SQLAPI++ doesn't change this option and uses the value set by OLEDB. See
                OLEDB documentation for details.
            </>
    },
    {
        name: `DBPROP_SERVERCURSOR, DBPROP_OTHERINSERT, DBPROP_OTHERUPDATEDELETE, 
               DBPROP_OWNINSERT, DBPROP_OWNUPDATEDELETE, DBPROP_REMOVEDELETED, DBPROP_CANSCROLLBACKWARDS`,
        scope: 'command',
        description:
            <>
                <p>
                    Forces SQL Server to return result sets using one of the following methods:
                </p>
                <ul>
                    <li>
                        Default result sets, which:
                        <ul>
                            <li>provide maximal performance in fetching data</li>
                            <li>support only one active statement at a time on a connection</li>
                        </ul>
                    </li>
                    <li>
                        Server cursors, which:
                        <ul>
                            <li>support multiple active statements on a single connection</li>
                            <li>can decrease performance relative to a default result set</li>
                        </ul>
                    </li>
                </ul>
                <p>
                    You can request different cursor behaviors in a rowset by setting rowset properties
                    {' '}<CodeBlend>DBPROP_SERVERCURSOR, DBPROP_OTHERINSERT, DBPROP_OTHERUPDATEDELETE, DBPROP_OWNINSERT,
                    DBPROP_OWNUPDATEDELETE, DBPROP_REMOVEDELETED</CodeBlend>. Some properties can be safely combined
                    with others. See OLEDB documentation to get more about how they affect SQL Server cursors.
                </p>
            </>,
        valid:
            <>
                <code>"VARIANT_TRUE"</code>, {' '}<code>"VARIANT_FALSE"</code>.
                For DBPROP_SERVERCURSOR value {' '}<code>"VARIANT_TRUE"</code>{' '}
                means OLEDB implements the rowset using a server cursor,
                {' '}<code>"VARIANT_FALSE"</code>{' '} - OLEDB implements
                the rowset using a default result set.
            </>,
        defaultValue:
            <>
                By default SQLAPI++ doesn't change this option and uses the value set by OLEDB. See
                OLEDB documentation for details
            </>
    }
];

export {
    options
};
